/**
 * Beat 10's authored sequence, and its fixed-step facts: Holly and one verb.
 *
 * The sibling of `woolShedPuzzle.ts`, and deliberately the same shape — a `Beat`
 * and the class the room steps, in one file. It knows authored points and
 * nothing else, so the decisions that can move an animal or raise a watcher's
 * suspicion are replayable in fixed time. No Three, no Rapier, no room
 * coordinates — `src/story/` does not import `src/world/`, so every authored
 * point arrives through `TwelveOutOptions`, every plane a flag is measured
 * against is `main.ts`'s, and the room is what states both.
 * `tests/e2e/beat-10-call.spec.ts` is what holds the two ends together.
 *
 * **The beat had no `BeatSequencer` at all until stage 6**, which is why it was
 * the one built beat in the game that could not end: `story` was null, so
 * `story?.finished` was never true, the boundary never committed, and walking
 * into the culvert did nothing. Everything above `STANDOFF_RADIUS` is that
 * omission being paid for.
 */

import { HOLLY_BODY_RADIUS } from '@/models/holly';
import { Companion, type CompanionObstacle, type CompanionSnapshot, type Vec2 } from '@/systems/companion';
import { CompanionBell, type CompanionBellEvent } from '@/systems/companionBell';
import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Which way the verb currently points. There is no third value on purpose.
 *
 * `F` is one key with two directions and both of them ring — call her to you,
 * send her back to her lie-up. There is no "stay", because a companion the
 * player can park is a companion whose noise they can stop making, and the
 * noise is the entire cost of the verb (`docs/beat-10-plan.md`, stage B).
 */
export type TwelveOutCall = 'lie-up' | 'with-her';

/**
 * The beat's interactable ids. One, and it is the headcount.
 *
 * Spec §7.10 wants the count *"visible only as a diegetic huddle you can look
 * at"*, so the verb is `observe` — three beats old by now — and the completion
 * writes a mark on her map and **nothing else**. There is no counter, no tally
 * and no "N of 12"; beat 8's hens established that rule and this is the beat
 * where the player finds out it still holds when there really is a number to
 * take. The number is theirs to carry and the game never repeats it back.
 */
export const TWELVE_OUT_IDS = {
  huddle: 'twelve-out.huddle',
} as const;

/**
 * What the ROOM tells the sequencer. Three facts, and none of them is a verb.
 *
 * Every one is a plane in `main.ts`, measured against her actual position on the
 * fixed step, because `src/story/` may not know where anything is. They are
 * raised into `storyFlags`, which only ever grows — so all three are sticky, and
 * `escapable` below depends on that being true of `inThePipe`.
 */
export const TWELVE_OUT_FLAGS = {
  /** South of the field fence: through the hole she made in beat 7 (D-173). */
  inTheField: 'twelve-out.inTheField',
  /** The string has actually left the huddle — `CulvertQueue`'s clock is running. */
  theyAreMoving: 'twelve-out.theyAreMoving',
  /** A body length inside the bore. `CULVERT_ENTER_X` is the plane and its own note is the derivation. */
  inThePipe: 'twelve-out.inThePipe',
} as const;

/**
 * Three cues, which is all the presentation this close has.
 *
 * `open` and `fadeOut` are `BeatFadeCues`; `end` is what stops the beat's two
 * loops. **There is deliberately no fourth.** `docs/beat-10-plan.md`, stage E:
 * *"No score screen, no survivor count, no slow-motion."* Beat 7 spent the
 * game's one authored look-back and repeating it here would cheapen it, so the
 * beat closes on a fade over a pipe she walked into herself.
 */
export const TWELVE_OUT_CUES = {
  open: 'twelve-out.open',
  fadeOut: 'twelve-out.fadeOut',
  end: 'twelve-out.end',
} as const;

/**
 * The four backstops, and D-097 is the whole reason they exist.
 *
 * *"Every stage ends on a clock as well as on its condition"*, because the
 * failure D-097 records is a stage that waits on a condition a stopped player
 * will never meet — and a stopped player is a different player from the one the
 * stage was authored for. **None of these is a fail state** (spec §5.6): they
 * end a stage, never the run, and nothing about the field changes when one
 * fires.
 *
 * They are generous on purpose. The beat's own clock is 52 s and these are two
 * to three times that, so a backstop firing is evidence the player is stuck
 * rather than slow — which is the only thing a backstop should ever mean.
 */
/** She has not gone through the boards. Six metres of holding forward, and D-173 says that is all it takes. */
export const FIELD_PATIENCE = 60;
/** She never looked at the twelve. The huddle stays observable afterwards; only the stage moves on. */
export const COUNT_PATIENCE = 90;
/** She never got the string moving — `F` unspent, or the dog still on the bank. */
export const MUSTER_PATIENCE = 120;
/** She is in the field long after the dogs arrived. The beat closes; it does not judge. */
export const RUN_PATIENCE = 150;
/**
 * The fade, and it is beat 9's number.
 *
 * `WOOL_SHED_REST_BEAT`'s `FADE_SECONDS` is 4 and this shares its
 * `FadeOverlay` ramp through `BeatFadeDirector`, so the two rooms close at one
 * speed rather than two that happen to agree.
 */
export const CLOSE_HOLD = 4.0;

/**
 * The no-input length of this beat, seconds, DERIVED and never written down.
 *
 * `OUT_NO_INPUT_SECONDS` is the sibling and its reason is this one's: the one
 * thing worse than a slow test is a slow test whose timeout was tuned to a
 * number nobody can find any more. `beat-10-close.spec.ts` asserts against this.
 */
export const TWELVE_OUT_NO_INPUT_SECONDS = FIELD_PATIENCE + COUNT_PATIENCE + MUSTER_PATIENCE + RUN_PATIENCE + CLOSE_HOLD;

/**
 * **The way out is always the way out.** The one rule here that is not beat 7's.
 *
 * A `BeatSequencer` runs its stages in order, so without this a player who
 * sprints past the twelve without counting them, never calls Holly, and reaches
 * the pipe in forty seconds is **standing inside a concrete culvert waiting out
 * `COUNT_PATIENCE`** — ninety seconds of a beat that is, in every sense the
 * player can see, over. That is not a hypothetical: it is how the stages were
 * written before this wrapper existed, and it is the exact shape of softlock
 * D-097 is about.
 *
 * Playing the beat badly is allowed (spec §5.6: no fail state, and
 * `docs/beat-10-plan.md` on the count being the player's to take or not).
 * Being detained for it is not.
 *
 * It is a wrapper rather than a clause repeated in each stage because a clause
 * repeated four times is a clause that will be three next session.
 * `STORY_TUNING.maxTransitionsPerUpdate` is 8, so the four transitions this can
 * cascade all happen inside one fixed tick.
 *
 * `beat-10-close.spec.ts`'s short-control arm is what makes it a conditional
 * rather than a claim: it holds her 1.5 m outside the plane and requires the
 * beat NOT to finish, and it was watched going red with stage 4 forced open.
 */
function escapable(stage: Stage): Stage {
  return {
    ...stage,
    done: (ctx: StoryContext): boolean => stage.done(ctx) || ctx.completed.has(TWELVE_OUT_FLAGS.inThePipe),
  };
}

export const TWELVE_OUT_BEAT: Beat = {
  number: 10,
  id: 'act2.twelveOut',
  stages: [
    escapable({
      id: 'the-field',
      enter: (_ctx, emit): void => emit(TWELVE_OUT_CUES.open),
      done: (ctx: StoryContext): boolean => ctx.completed.has(TWELVE_OUT_FLAGS.inTheField) || ctx.stageTime >= FIELD_PATIENCE,
    }),
    escapable({
      id: 'the-count',
      done: (ctx: StoryContext): boolean => ctx.completed.has(TWELVE_OUT_IDS.huddle) || ctx.stageTime >= COUNT_PATIENCE,
    }),
    escapable({
      id: 'the-dog',
      done: (ctx: StoryContext): boolean => ctx.completed.has(TWELVE_OUT_FLAGS.theyAreMoving) || ctx.stageTime >= MUSTER_PATIENCE,
    }),
    {
      id: 'the-run',
      done: (ctx: StoryContext): boolean => ctx.completed.has(TWELVE_OUT_FLAGS.inThePipe) || ctx.stageTime >= RUN_PATIENCE,
    },
    {
      id: 'close',
      enter: (_ctx, emit): void => emit(TWELVE_OUT_CUES.fadeOut),
      done: (ctx: StoryContext): boolean => ctx.stageTime >= CLOSE_HOLD,
      exit: (_ctx, emit): void => emit(TWELVE_OUT_CUES.end),
    },
  ],
};

/**
 * Where she stops, and it is the shed's 1.05 m widened rather than reused.
 *
 * Outdoors she is not threading a doorway — the shed's number was set by the
 * width of one, and `WOOL_SHED_DOORWAY` is why it is as tight as it is. 1.50 m
 * is a companion standing beside her in an open field rather than at her heel.
 *
 * **What she actually stops at is 1.70 m**, because `Companion.#moveToward`
 * halts within `arrivalRadius` of a target that is already `standoffRadius`
 * from Rosalie. That is not a defect and the spec asserts the sum rather than
 * this constant alone — `docs/traps.md`: derive an expected value from the
 * exported constant the code reads, never from arithmetic done in your head.
 */
export const STANDOFF_RADIUS = 1.50;

/**
 * Her walking speed, and it is beat 9's exactly.
 *
 * The open field is an argument for making her quicker and it is the wrong
 * argument: **the call costs seconds, and the seconds are the spend.** A player
 * who can summon her instantly is not trading anything. It is also the number
 * `HOLLY_WALK_STRIDE` in `main.ts` is derived from, so the two rooms drive her
 * walk clip off one speed rather than two that happen to agree.
 */
export const MOVE_SPEED = 1.15;

/** Per-second convergence once she is close enough that top speed no longer governs. */
export const SMOOTHING = 5.0;

/** How close to a target she comes to rest. Read the note on `STANDOFF_RADIUS`. */
export const ARRIVAL_RADIUS = 0.20;

export interface TwelveOutSnapshot {
  readonly holly: CompanionSnapshot;
  /** Which direction the verb is currently sending her. */
  readonly call: TwelveOutCall;
  /** True while she is actually covering ground under the current call. */
  readonly travelling: boolean;
}

/** The two clear sides of the one field-fence opening, authored by the room. */
export interface CompanionGap {
  /** The opening's horizontal span, retained so the room can validate its own route. */
  readonly minX: number;
  readonly maxX: number;
  /** The fence line which decides whether a route is needed. */
  readonly fenceZ: number;
  /** A clear point within the opening on each side of that line. */
  readonly north: Vec2;
  readonly south: Vec2;
}

export interface TwelveOutOptions {
  /** Her authored start, and the station `F` sends her back to. From the room. */
  readonly lieUp: { readonly x: number; readonly z: number; readonly yaw: number };
  /** The room's solids she routes around. Real footprints; the companion inflates them. */
  readonly obstacles: readonly CompanionObstacle[];
  /** The one opening that lets a call cross the field fence. From the room. */
  readonly gap: CompanionGap;
  readonly bellStrideDistance: number;
  readonly bellSoundRadius: number;
}

export class TwelveOut {
  readonly #options: TwelveOutOptions;
  readonly #companion: Companion;
  readonly #bell: CompanionBell;
  #call: TwelveOutCall = 'lie-up';

  constructor(options: TwelveOutOptions) {
    this.#options = options;
    this.#companion = this.#createCompanion();
    this.#bell = new CompanionBell(
      { strideDistance: options.bellStrideDistance, soundRadius: options.bellSoundRadius },
      this.#companion.snapshot(),
    );
  }

  #createCompanion(): Companion {
    return new Companion({
      // **`phase: 'following'` from construction and it never changes.** Beat 9's
      // transitions — `release`, `beginHighLatch`, `beginRest` — are the shed's
      // puzzle and none of them is reachable from here. `Companion.step` only
      // moves her while she is following, so this is the whole of her state
      // machine out here: one phase, and a target that is either Rosalie or a
      // station.
      initial: { ...this.#options.lieUp, phase: 'following' },
      standoffRadius: STANDOFF_RADIUS,
      moveSpeed: MOVE_SPEED,
      smoothing: SMOOTHING,
      arrivalRadius: ARRIVAL_RADIUS,
      // There are none out here, and the empty list is authored rather than
      // absent: `Companion.step` holds her still on any tick Rosalie stands in a
      // doorway, which in a field would be a companion that stopped for no
      // visible reason.
      doorways: [],
      stations: [
        { id: 'lie-up', target: this.#options.lieUp },
        { id: 'gap-north', target: this.#options.gap.north },
        { id: 'gap-south', target: this.#options.gap.south },
      ],
      bodyRadius: HOLLY_BODY_RADIUS,
      obstacles: this.#options.obstacles,
    });
  }

  snapshot(): TwelveOutSnapshot {
    const holly = this.#companion.snapshot();
    return { holly, call: this.#call, travelling: holly.moving };
  }

  /** The `F` verb. Flips the direction and returns the one now in force. */
  toggleCall(): TwelveOutCall {
    this.#call = this.#call === 'lie-up' ? 'with-her' : 'lie-up';
    return this.#call;
  }

  /**
   * One fixed step. Returns the cadence marks she crossed during it.
   *
   * **The bell is fed every tick this is called, including the still ones.**
   * `CompanionBell.advance` validates that each pair continues the trace it last
   * accepted, so a tick that skipped it is an exception on the next one rather
   * than a bell that quietly did not ring. That is why the pair is taken here,
   * around this one `step`, and why nothing outside this class may call
   * `advance` on `#bell`.
   */
  step(dt: number, rosalie: Vec2): readonly CompanionBellEvent[] {
    const before = this.#companion.snapshot();
    const target = this.#targetFor(rosalie);
    const gapStation = this.#gapStationFor(before, target);
    const after = this.#companion.step(dt, rosalie, gapStation === null ? this.#call === 'lie-up'
      // `atStation` ignores the standoff and seeks the nearest allowed station;
      // there is exactly one, so "send her back" is this line and nothing else.
      ? { atStation: true, stationIds: ['lie-up'] }
      : { atStation: false }
      : { atStation: true, stationIds: [gapStation] });
    return this.#bell.advance(before, after);
  }

  /**
   * Where the current call is sending her, to the nearest side of the fence.
   *
   * **It is Rosalie herself and not her standoff point**, which is a deliberate
   * simplification and the reason is that the first version was not: it
   * reconstructed `Companion.#followTarget`'s arithmetic — the normalised offset
   * at `STANDOFF_RADIUS`, the yaw fallback for a zero-length vector, all of it —
   * so that this could ask which side the *exact* target was on. That is a
   * second statement of a computation that lives one file away, and this repo
   * has paid for two of those already (D-116, and the stock fence's wires).
   *
   * All `#gapStationFor` needs is a SIDE, and the standoff is 1.50 m against a
   * fence Holly is held 0.35 m clear of, so the only trace where the two answers
   * could differ is one where Rosalie stands within a standoff of the fence line
   * — and there, routing her through the hole is the right answer under either
   * reading. The simplification cannot pick the wrong side of a wall; it can at
   * most send her through a hole she was going to walk past.
   */
  #targetFor(rosalie: Vec2): Vec2 {
    return this.#call === 'lie-up' ? this.#options.lieUp : rosalie;
  }

  /**
   * Companion only slides around solids; it cannot discover which end of a long
   * fence is open. Send a crossing call through the authored hole first, while
   * retaining the fence runs as collision backstops rather than teleporting her.
   */
  #gapStationFor(holly: CompanionSnapshot, target: Vec2): 'gap-north' | 'gap-south' | null {
    const hollySide = holly.z - this.#options.gap.fenceZ;
    const targetSide = target.z - this.#options.gap.fenceZ;
    if (hollySide * targetSide >= 0) return null;
    return targetSide > 0 ? 'gap-north' : 'gap-south';
  }

  /**
   * Her authored start, the bell's cadence and the verb, restored together.
   *
   * **The two resets are one statement and that is the entire point of this
   * method.** `Companion.reset()` puts `travelledDistance` back to its initial
   * value while `CompanionBell` keeps the trace it last accepted, so a reset
   * that touched only the companion makes the very next `advance()` throw
   * *"previous travelledDistance does not match accepted trace"*. Beat 9 hit
   * exactly this, could not fix it — `WoolShedPuzzle.reset()` has no caller —
   * and filed it against this beat (`docs/backlog.md`). Its caller here is
   * `respawn()` in `main.ts`, which is the path stage 3's capture will take.
   *
   * The call direction goes back to `'lie-up'` with them: a respawn that left
   * the verb pointing at a Rosalie who is no longer where she was would send
   * Holly across the field to a place the player never chose.
   */
  reset(): void {
    this.#companion.reset();
    this.#bell.reset(this.#companion.snapshot());
    this.#call = 'lie-up';
  }
}
